import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useVendorAuth } from "../../store/vendorAuthStore";
import { PriceCalculator } from "./AddProduct";
import "./products.css";

const API_BASE = "https://xiadot.com/vendor_api";

const UNIT_OPTIONS = [
  { value: "piece", label: "Piece / Qty" },
  { value: "gram", label: "Gram (g)" },
  { value: "kilogram", label: "Kilogram (kg)" },
  { value: "litre", label: "Litre (L)" },
  { value: "millilitre", label: "Millilitre (ml)" },
  { value: "dozen", label: "Dozen" },
  { value: "pack", label: "Pack" },
  { value: "metre", label: "Metre (m)" },
  { value: "size", label: "Size" },
  { value: "custom", label: "Custom" },
];

const COLOR_OPTIONS = [
  { value: "Red", label: "Red", hex: "#FF0000" },
  { value: "Blue", label: "Blue", hex: "#0000FF" },
  { value: "Green", label: "Green", hex: "#00FF00" },
  { value: "Yellow", label: "Yellow", hex: "#FFFF00" },
  { value: "Black", label: "Black", hex: "#000000" },
  { value: "White", label: "White", hex: "#FFFFFF" },
  { value: "Pink", label: "Pink", hex: "#FFC0CB" },
  { value: "Purple", label: "Purple", hex: "#800080" },
  { value: "Orange", label: "Orange", hex: "#FFA500" },
  { value: "Brown", label: "Brown", hex: "#964B00" },
  { value: "Gray", label: "Gray", hex: "#808080" },
  { value: "Navy", label: "Navy", hex: "#000080" },
  { value: "Cream", label: "Cream", hex: "#FFFDD0" },
  { value: "Maroon", label: "Maroon", hex: "#800000" },
  { value: "Teal", label: "Teal", hex: "#008080" },
  { value: "Silver", label: "Silver", hex: "#C0C0C0" },
  { value: "Gold", label: "Gold", hex: "#FFD700" },
  { value: "Lime", label: "Lime", hex: "#32CD32" },
  { value: "Coral", label: "Coral", hex: "#FF7F50" },
  { value: "Indigo", label: "Indigo", hex: "#4B0082" },
];

const PREDEFINED_COLOR_VALUES = COLOR_OPTIONS.map((c) => c.value);

async function tryFetch(urls, headers) {
  for (let i = 0; i < urls.length; i++) {
    try {
      const res = await fetch(urls[i], { headers });
      if (!res.ok) continue;
      const data = await res.json().catch(() => null);
      if (data) return data;
    } catch { /* try next */ }
  }
  return null;
}


export default function EditProduct() {
  const { token } = useVendorAuth();
  const { id } = useParams();
  const nav = useNavigate();
  const authHeaders = token ? { Authorization: "Bearer " + token } : {};

  // ✅ Ref to prevent sub-cat effect from overwriting product data after load
  const productLoadedRef = useRef(false);
  const productDataSetRef = useRef(false); // Additional ref to track if form data has been set

  const [form, setForm] = useState({
    name: "",
    description: "",
    mrp: "",
    offer_price: "",
    delivery_charge: "",
    unit: "piece",
    unit_qty: "1",
    stock: "",
    main_category_id: "",
    sub_category_id: "",
    customized_values: [""],
    color: "",
    custom_color: "",
  });

  const [mainCats, setMainCats] = useState([]);
  const [subCats, setSubCats] = useState([]);
  const [catsLoading, setCatsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const [existingImages, setExistingImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [deletedUrls, setDeletedUrls] = useState([]);

  // Test API directly - CRITICAL DEBUG
  useEffect(() => {
    if (id && token) {
      console.log("=== CRITICAL API TEST START ===");
      fetch(`${API_BASE}/user/get-product-details.php?id=${id}`, {
        headers: { Authorization: "Bearer " + token }
      })
        .then(res => {
          console.log("API Response Status:", res.status);
          return res.json();
        })
        .then(data => {
          console.log("=== FULL API RESPONSE ===");
          console.log(JSON.stringify(data, null, 2));
          console.log("=== EXTRACTED DATA ===");
          if (data.success && data.data) {
            console.log("Success: true");
            console.log("Product ID:", data.data.id);
            console.log("Product Name:", data.data.name);
            console.log("Customized Values:", data.data.customized_values);
            console.log("Customized Values Type:", typeof data.data.customized_values);
            console.log("Color:", data.data.color);
            console.log("Color Type:", typeof data.data.color);
          } else {
            console.log("API ERROR:", data.message);
          }
          console.log("=== CRITICAL API TEST END ===");
        })
        .catch(err => {
          console.error("=== API CALL FAILED ===");
          console.error(err);
        });
    }
  }, [id, token]);

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setDirect = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const selectedMainCat = mainCats.find((c) => String(c.id) === String(form.main_category_id)) || null;
  const selectedCommission = selectedMainCat ? selectedMainCat.commission : null;

  // ── Load Main Categories ─────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setCatsLoading(true);
      const data = await tryFetch([
        `${API_BASE}/vendor/main_categories/list.php`,
        `${API_BASE}/admin/main_categories/list.php`,
        `${API_BASE}/main_categories/list.php`,
      ], authHeaders);
      if (data) {
        const list = data.categories || data.data || data.main_categories || [];
        setMainCats((Array.isArray(list) ? list : []).map((c) => ({
          id: String(c.id ?? c.category_id ?? ""),
          label: String(c.name || c.category_name || c.title || ""),
          commission:
            c.commission != null ? Number(c.commission)
              : c.commission_percent != null ? Number(c.commission_percent)
                : c.commission_percentage != null ? Number(c.commission_percentage)
                  : null,
        })));
      }
      setCatsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // ── Load Sub Categories ──────────────────────────────────────────
  useEffect(() => {
    const mid = String(form.main_category_id || "");
    if (!mid) { setSubCats([]); return; }
    (async () => {
      const data = await tryFetch([
        `${API_BASE}/vendor/sub_categories/list.php`,
        `${API_BASE}/admin/sub_categories/list.php`,
        `${API_BASE}/sub_categories/list.php`,
      ], authHeaders);
      if (data) {
        const raw = data.subcategories || data.sub_categories || data.data || [];
        const normalized = (Array.isArray(raw) ? raw : []).map((x) => ({
          id: String(x.id ?? x.sub_id ?? ""),
          category_id: String(x.category_id ?? x.main_category_id ?? ""),
          label: String(x.name ?? x.sub_category_name ?? x.title ?? ""),
        }));
        const filtered = normalized.filter((x) => x.category_id === mid);
        setSubCats(filtered);

        // ✅ KEY FIX: Only auto-select first sub-cat if product hasn't loaded yet.
        // After product loads, we NEVER auto-reset sub_category_id or any other field.
        if (!productLoadedRef.current && !productDataSetRef.current) {
          setForm((prev) => {
            const ok = filtered.some((x) => x.id === String(prev.sub_category_id));
            console.log("SubCat Effect - Product not loaded, updating sub_category_id:", {
              current: prev.sub_category_id,
              willSet: ok ? prev.sub_category_id : filtered[0]?.id || ""
            });
            return ok ? prev : { ...prev, sub_category_id: filtered[0]?.id || "" };
          });
        } else {
          console.log("SubCat Effect - Product already loaded, NOT touching form data", {
            productLoaded: productLoadedRef.current,
            productDataSet: productDataSetRef.current
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.main_category_id, token]);

  // ── Load Product ─────────────────────────────────────────────────
  useEffect(() => {
    productLoadedRef.current = false; // reset on new product load
    productDataSetRef.current = false; // reset on new product load
    (async () => {
      setLoading(true); setErr("");
      try {
        const res = await fetch(`${API_BASE}/user/get-product-details.php?id=${id}`);
        const data = await res.json();
        if (!data.success) throw new Error(data.message || "Product not found");
        const p = data.data;

        // Debug: Log what we're receiving from API
        console.log("EditProduct - Raw API data:", p);
        console.log("EditProduct - customized_values from API:", p.customized_values);
        console.log("EditProduct - color from API:", p.color);

        // Simplified data loading - handle customized_values and color directly
        let customizedValues = [""];
        if (p.customized_values) {
          if (Array.isArray(p.customized_values)) {
            customizedValues = p.customized_values.filter(v => v != null && String(v).trim() !== "");
          } else if (typeof p.customized_values === "string") {
            try {
              const parsed = JSON.parse(p.customized_values);
              if (Array.isArray(parsed)) {
                customizedValues = parsed.filter(v => v != null && String(v).trim() !== "");
              }
            } catch {
              // If JSON parse fails, treat as comma-separated
              if (p.customized_values.includes(",")) {
                customizedValues = p.customized_values.split(",").map(v => v.trim()).filter(v => v);
              } else if (p.customized_values.trim()) {
                customizedValues = [p.customized_values.trim()];
              }
            }
          }
        }
        if (customizedValues.length === 0) customizedValues = [""];

        // Handle color - simple logic
        const savedColor = (p.color || "").trim();
        const isPredefined = PREDEFINED_COLOR_VALUES.includes(savedColor);

        const formData = {
          name: p.name || "",
          description: p.description || "",
          mrp: p.price != null ? String(p.price) : "",
          offer_price: p.offer_price != null ? String(p.offer_price) : "",
          delivery_charge: p.delivery_charge != null ? String(p.delivery_charge) : "",
          unit: p.unit || "piece",
          unit_qty: p.unit_qty != null ? String(p.unit_qty) : "1",
          stock: p.stock != null ? String(p.stock) : "",
          main_category_id: String(p.main_category_id || p.category_id || ""),
          sub_category_id: String(p.sub_category_id || p.category_id || ""),
          customized_values: customizedValues,
          color: isPredefined ? savedColor : "",
          custom_color: !isPredefined && savedColor ? savedColor : "",
        };

        console.log("EditProduct - Setting form data:", formData);
        setForm(formData);

        // Check if form state gets overwritten after 2 seconds
        setTimeout(() => {
          console.log("EditProduct - Form state after 2 seconds:", {
            customized_values: form.customized_values,
            color: form.color,
            custom_color: form.custom_color,
          });
        }, 2000);

        setExistingImages(Array.isArray(p.images) ? p.images.filter(Boolean) : []);
        setNewImages([]);
        setDeletedUrls([]);
      } catch (e) {
        setErr(e.message || "Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  // Debug: Monitor form state changes
  useEffect(() => {
    console.log("EditProduct - Form state updated:", {
      customized_values: form.customized_values,
      color: form.color,
      custom_color: form.custom_color,
    });
  }, [form.customized_values, form.color, form.custom_color]);

  // ── Image handlers ───────────────────────────────────────────────
  function deleteExisting(url) {
    setExistingImages((p) => p.filter((u) => u !== url));
    setDeletedUrls((p) => [...p, url]);
  }
  function removeNew(idx) { setNewImages((p) => p.filter((_, i) => i !== idx)); }
  function handleFilePick(files) {
    Array.from(files || []).forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => { setNewImages((p) => [...p, { file, preview: e.target.result }]); };
      reader.readAsDataURL(file);
    });
  }

  // ── Submit ───────────────────────────────────────────────────────
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!form.name.trim()) { setErr("Product name is required"); return; }
    if (!Number(form.mrp)) { setErr("MRP / price is required"); return; }

    const hasCustomizedValues = form.customized_values.some(
      (val) => val && val.trim() !== ""
    );

    // Only require main offer price for non-variant products
    if (!hasCustomizedValues) {
      if (!Number(form.offer_price)) {
        setErr("Offer price is required");
        return;
      }
    }
    
    if (Number(form.offer_price) > 0 && Number(form.offer_price) > Number(form.mrp)) {
      setErr("Offer price must be less than MRP");
      return;
    }

    if (existingImages.length + newImages.length === 0) {
      setErr("At least one product image is required");
      return;
    }

    const fd = new FormData();
    fd.append("id", String(id));
    fd.append("main_category_id", String(form.main_category_id));
    fd.append("category_id", String(form.sub_category_id));
    fd.append("sub_category_id", String(form.sub_category_id));
    fd.append("name", form.name.trim());
    fd.append("description", (form.description || "").trim());
    fd.append("price", String(Number(form.mrp || 0)));
    fd.append("offer_price", String(Number(form.offer_price || 0)));
    fd.append("delivery_charge", String(Number(form.delivery_charge || 0)));
    fd.append("unit", form.unit || "piece");
    fd.append("unit_qty", String(Number(form.unit_qty || 1)));
    fd.append("stock", String(Number(form.stock || 0)));
    fd.append("is_active", "1");

    const validCustomizedValues = form.customized_values.filter((val) => val.trim() !== "");
    if (validCustomizedValues.length > 0) {
      fd.append("customized_values", JSON.stringify(validCustomizedValues));
    }

    const finalColor = form.custom_color.trim() || form.color.trim();
    if (finalColor) {
      fd.append("color", finalColor);
    }

    deletedUrls.forEach((url) => { fd.append("deleted_images[]", url); });
    newImages.forEach((img) => { fd.append("images[]", img.file); });

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/vendor/products/update.php`, { method: "POST", headers: authHeaders, body: fd });
      const text = await res.text();
      let data = {}; try { data = JSON.parse(text); } catch { }
      if (!res.ok || data.success === false) { setErr(data.message || text || "Update failed"); return; }
      nav(-1);
    } catch (e) {
      setErr(e.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="vp-edit-loading">
      <div className="vp-spinner" />
      <p>Loading product…</p>
    </div>
  );

  const totalCount = existingImages.length + newImages.length;

  // Debug: Add test display to verify form data
  console.log("EditProduct - Rendering form with data:", {
    customized_values: form.customized_values,
    color: form.color,
    custom_color: form.custom_color,
  });


  return (
    <div className="vp-edit-page">
      {/* Debug Display - Remove this after fixing */}
      <div style={{
        position: "fixed",
        top: 10,
        right: 10,
        background: "yellow",
        padding: "10px",
        zIndex: 9999,
        fontSize: "12px",
        border: "1px solid red"
      }}>
        <div>Customized: {JSON.stringify(form.customized_values)}</div>
        <div>Color: {form.color}</div>
        <div>Custom Color: {form.custom_color}</div>
      </div>

      {/* Test Button - Remove this after fixing */}
      <div style={{
        position: "fixed",
        top: 120,
        right: 10,
        background: "orange",
        padding: "10px",
        zIndex: 9999,
        fontSize: "12px",
        border: "1px solid red"
      }}>
        <button
          onClick={() => {
            console.log("TEST BUTTON CLICKED - Setting hardcoded data");
            setForm({
              ...form,
              customized_values: ["HARDcoded_1", "HARDcoded_2"],
              color: "HARDCODED_COLOR",
              custom_color: "",
            });
          }}
          style={{ marginBottom: "5px", padding: "5px" }}
        >
          Set Hardcoded Data
        </button>
        <div>This tests if form can display data at all</div>
      </div>

      {/* CRITICAL TEST - Bypass API completely */}
      <div style={{
        position: "fixed",
        top: 200,
        right: 10,
        background: "red",
        padding: "10px",
        zIndex: 9999,
        fontSize: "12px",
        border: "1px solid yellow",
        color: "white"
      }}>
        <button
          onClick={() => {
            console.log("=== BYPASS TEST - Setting complete test data ===");
            const completeTestData = {
              name: "TEST PRODUCT NAME",
              description: "Test description",
              mrp: "100",
              offer_price: "80",
              delivery_charge: "10",
              unit: "piece",
              unit_qty: "1",
              stock: "50",
              main_category_id: "1",
              sub_category_id: "1",
              customized_values: ["Size M", "Size L", "Size XL"],
              color: "Blue",
              custom_color: "",
            };
            console.log("Setting complete test data:", completeTestData);
            setForm(completeTestData);
          }}
          style={{ marginBottom: "5px", padding: "5px", background: "white", color: "black" }}
        >
          COMPLETE TEST DATA
        </button>
        <div>Bypasses API - tests full form</div>
      </div>
      <div className="vp-edit-header">
        <button className="vp-back-btn" onClick={() => nav(-1)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
          </svg>
          Back
        </button>
        <div className="vp-edit-header-title">
          <div className="vp-header-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </div>
          <div>
            <h1 className="vp-title">Edit Product</h1>
            <p className="vp-subtitle">Update product details &amp; images</p>
          </div>
        </div>
      </div>

      <div className="vp-edit-card" style={{ maxWidth: 800 }}>
        {err && (
          <div className="vp-form-err" style={{ marginBottom: 20 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}>
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {err}
          </div>
        )}

        <form onSubmit={submit}>

          {/* Category */}
          <div className="vp-section-title">Category</div>
          <div className="vp-edit-grid">
            <div className="vp-form-group">
              <label className="vp-label">Main Category</label>
              <select className="vp-input vp-select" value={String(form.main_category_id || "")}
                disabled={catsLoading}
                onChange={(e) => {
                  // ✅ When vendor manually changes category, allow sub-cat reset
                  productLoadedRef.current = false;
                  productDataSetRef.current = false;
                  setDirect("main_category_id", e.target.value);
                  setDirect("sub_category_id", "");
                }}>
                <option value="">{catsLoading ? "Loading…" : "Select main category…"}</option>
                {mainCats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}{c.commission != null ? ` (${c.commission}% commission)` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="vp-form-group">
              <label className="vp-label">Sub Category</label>
              <select className="vp-input vp-select" value={String(form.sub_category_id || "")}
                disabled={!form.main_category_id || catsLoading}
                onChange={(e) => setDirect("sub_category_id", e.target.value)}>
                <option value="">{!form.main_category_id ? "Select main first" : "Select sub category…"}</option>
                {subCats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Product Info */}
          <div className="vp-section-title" style={{ marginTop: 20 }}>Product Info</div>
          <div className="vp-form-group">
            <label className="vp-label">Product Name *</label>
            <input className="vp-input" value={form.name} onChange={setField("name")} placeholder="Product name" required />
          </div>
          <div className="vp-form-group" style={{ marginTop: 14 }}>
            <label className="vp-label">Description <span className="vp-label-opt">(optional)</span></label>
            <textarea className="vp-input vp-textarea" value={form.description}
              onChange={setField("description")} rows={3} placeholder="Describe your product…" />
          </div>

          {/* Measurement */}
          <div className="vp-section-title" style={{ marginTop: 20 }}>Measurement Unit</div>
          <div className="vp-edit-grid">
            <div className="vp-form-group">
              <label className="vp-label">Unit Type</label>
              <select className="vp-input vp-select" value={form.unit} onChange={setField("unit")}>
                {UNIT_OPTIONS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
            <div className="vp-form-group">
              <label className="vp-label">Qty per stock item <span className="vp-label-opt">(e.g. 500 for 500g)</span></label>
              <input className="vp-input" value={form.unit_qty} onChange={setField("unit_qty")} placeholder="1" />
            </div>
          </div>

          {/* Customized Values */}
          <div className="vp-form-group" style={{ marginTop: 14 }}>
            <label className="vp-label">
              Customized Values <span className="vp-label-opt">(e.g. 7 inch, 8 inch, 9 inch for sizes)</span>
            </label>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {form.customized_values.map((value, index) => (
                <div key={index} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    className="vp-input"
                    placeholder="Enter customized value (e.g. 7 inch)"
                    value={value}
                    onChange={(e) => {
                      const newValues = [...form.customized_values];
                      newValues[index] = e.target.value;
                      setDirect("customized_values", newValues);
                    }}
                    style={{ flex: 1 }}
                  />
                  {form.customized_values.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const newValues = form.customized_values.filter((_, i) => i !== index);
                        setDirect("customized_values", newValues);
                      }}
                      style={{
                        width: 32, height: 32, borderRadius: 6, border: "1px solid #e2e8f0",
                        background: "#fff", color: "#ef4444", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}
                      title="Remove this value"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => setDirect("customized_values", [...form.customized_values, ""])}
                style={{
                  width: "100%", padding: "8px 12px", borderRadius: 6,
                  border: "1px dashed #3b82f6", background: "#eff6ff",
                  color: "#3b82f6", cursor: "pointer", fontSize: 13,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Customized Value
              </button>
            </div>
          </div>

          {/* Color Selection */}
          <div className="vp-form-group" style={{ marginTop: 14 }}>
            <label className="vp-label">
              Color <span className="vp-label-opt">(optional — choose predefined or enter custom)</span>
            </label>

            {(form.color || form.custom_color) && (
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "#f0fdf4", border: "1px solid #bbf7d0",
                borderRadius: 8, padding: "6px 12px", marginBottom: 10, fontSize: 12.5,
              }}>
                {(() => {
                  const activeColor = form.color || form.custom_color;
                  const match = COLOR_OPTIONS.find((c) => c.value === activeColor);
                  return (
                    <>
                      {match && (
                        <div style={{
                          width: 14, height: 14, borderRadius: 3,
                          background: match.hex,
                          border: (match.hex === "#FFFFFF" || match.hex === "#FFFDD0") ? "1px solid #d1d5db" : "none",
                        }} />
                      )}
                      <span style={{ color: "#15803d", fontWeight: 600 }}>
                        Current: {activeColor}
                        {form.custom_color ? " (custom)" : ""}
                      </span>
                    </>
                  );
                })()}
                <button
                  type="button"
                  onClick={() => { setDirect("color", ""); setDirect("custom_color", ""); }}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626", padding: 0, lineHeight: 1 }}
                  title="Clear color"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Predefined Colors */}
              <div>
                <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, display: "block" }}>
                  Choose from predefined colors:
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 8 }}>
                  {COLOR_OPTIONS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => {
                        setDirect("color", color.value);
                        setDirect("custom_color", "");
                      }}
                      style={{
                        padding: "8px 12px",
                        border: form.color === color.value
                          ? "2px solid #3b82f6"
                          : "1px solid #d1d5db",
                        borderRadius: 6,
                        background: form.color === color.value ? "#eff6ff" : "#fff",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 13,
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{
                        width: 16, height: 16, borderRadius: 3,
                        backgroundColor: color.hex,
                        border: (color.hex === "#FFFFFF" || color.hex === "#FFFDD0") ? "1px solid #d1d5db" : "none",
                        flexShrink: 0,
                      }} />
                      {color.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Color Input */}
              <div>
                <label style={{ fontSize: 12, color: "#6b7280", marginBottom: 6, display: "block" }}>
                  Or enter custom color name:
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    className="vp-input"
                    placeholder="e.g., Peach, Lavender, Turquoise"
                    value={form.custom_color}
                    onChange={(e) => {
                      setDirect("custom_color", e.target.value);
                      setDirect("color", "");
                    }}
                    style={{ flex: 1 }}
                  />
                  {form.custom_color && (
                    <button
                      type="button"
                      onClick={() => setDirect("custom_color", "")}
                      style={{
                        width: 32, height: 32, borderRadius: 6, border: "1px solid #e2e8f0",
                        background: "#fff", color: "#ef4444", cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                      }}
                      title="Clear custom color"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="vp-section-title" style={{ marginTop: 20 }}>Pricing &amp; Stock</div>
          <div className="vp-edit-grid">
            <div className="vp-form-group">
              <label className="vp-label">MRP (₹) *</label>
              <input className="vp-input" value={form.mrp} onChange={setField("mrp")} type="number" placeholder="0.00" required />
            </div>
            <div className="vp-form-group">
              <label className="vp-label">Offer Price (₹) <span className="vp-label-opt">(optional)</span></label>
              <input className="vp-input" value={form.offer_price} onChange={setField("offer_price")} type="number" placeholder="0.00" />
            </div>
            <div className="vp-form-group">
              <label className="vp-label">Delivery Charge (₹) <span className="vp-label-opt">(optional)</span></label>
              <input className="vp-input" value={form.delivery_charge} onChange={setField("delivery_charge")} type="number" placeholder="0.00" />
            </div>
            <div className="vp-form-group">
              <label className="vp-label">Stock</label>
              <input className="vp-input" value={form.stock} onChange={setField("stock")} type="number" placeholder="0" required />
            </div>
          </div>

          <PriceCalculator
            mrp={form.mrp}
            offerPrice={form.offer_price}
            deliveryCharge={form.delivery_charge}
            commission={selectedCommission}
          />

          {/* Images */}
          <div className="vp-section-title" style={{ marginTop: 20 }}>
            Product Images
            <span className="vp-label-opt" style={{ marginLeft: 8, textTransform: "none", letterSpacing: 0 }}>
              {totalCount} photo{totalCount !== 1 ? "s" : ""}
              {deletedUrls.length > 0 && (
                <span style={{ color: "var(--red)", marginLeft: 6 }}>· {deletedUrls.length} marked for deletion</span>
              )}
            </span>
          </div>

          {existingImages.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 11.5, color: "var(--text-3)", marginBottom: 8 }}>Saved photos — click ✕ to remove</p>
              <div className="vp-img-preview-grid">
                {existingImages.map((url, idx) => (
                  <div key={url} className="vp-img-slot vp-img-slot-filled">
                    <img src={url} alt={"existing-" + idx} className="vp-img-slot-preview"
                      onError={(e) => { e.currentTarget.src = "https://placehold.co/120x120/f5f5f5/999?text=?"; }} />
                    {idx === 0 && <span className="vp-img-main-badge">Main</span>}
                    <button type="button" className="vp-img-slot-remove" title="Remove this image" onClick={() => deleteExisting(url)}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {newImages.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ fontSize: 11.5, color: "var(--text-3)", marginBottom: 8 }}>New photos — will be added on save</p>
              <div className="vp-img-preview-grid">
                {newImages.map((img, idx) => (
                  <div key={idx} className="vp-img-slot vp-img-slot-filled" style={{ outline: "2px solid var(--green)", outlineOffset: 2 }}>
                    <img src={img.preview} alt={"new-" + idx} className="vp-img-slot-preview" />
                    <span className="vp-img-main-badge" style={{ background: "var(--green)" }}>New</span>
                    <button type="button" className="vp-img-slot-remove" title="Remove new photo" onClick={() => removeNew(idx)}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <label className="vp-bulk-upload-btn">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" />
            </svg>
            Add More Photos
            <input type="file" accept="image/*" multiple style={{ display: "none" }}
              onChange={(e) => { handleFilePick(e.target.files); e.target.value = ""; }} />
          </label>
          <p style={{ fontSize: 11.5, color: "var(--text-3)", marginTop: 8 }}>
            ✕ on saved photos removes them. New photos are added on top. No limit.
          </p>

          {/* Footer */}
          <div className="vp-edit-footer">
            <button type="button" className="vp-btn-cancel" onClick={() => nav(-1)}>Cancel</button>
            <button type="submit" className="vp-btn-save vp-btn-update" disabled={saving}>
              {saving ? (
                <><span className="vp-btn-spinner" /> Updating…</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Update Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}